import {
  INDICATOR_PANE_EXPANDED_HEIGHT_PX,
  INDICATOR_PANE_HEIGHT_PX,
  INDICATOR_PANE_MIN_HEIGHT_PX,
  MAIN_PANE_MIN_SHARE,
} from "../constants";

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));
const sum = (values) => values.reduce((total, value) => total + value, 0);

/**
 * Heights that fill `areaHeight`: in proportion to the wanted ones, but not
 * below the minimum; a pane held at the minimum leaves the rest to the others.
 * When even the minimums do not fit, all panes get it (the area scrolls).
 */
const shrinkHeights = (wanted, areaHeight) => {
  const isAtMinimum = wanted.map(() => false);

  for (;;) {
    const minimumCount = isAtMinimum.filter(Boolean).length;
    const freeHeight = areaHeight - minimumCount * INDICATOR_PANE_MIN_HEIGHT_PX;
    const freeWanted = sum(wanted.filter((height, position) => !isAtMinimum[position]));
    const ratio = freeWanted ? Math.max(0, freeHeight) / freeWanted : 0;
    const tooLow = wanted.filter(
      (height, position) => !isAtMinimum[position] && height * ratio < INDICATOR_PANE_MIN_HEIGHT_PX,
    );

    if (!tooLow.length) {
      return wanted.map((height, position) =>
        isAtMinimum[position] ? INDICATOR_PANE_MIN_HEIGHT_PX : Math.floor(height * ratio),
      );
    }

    wanted.forEach((height, position) => {
      if (height * ratio < INDICATOR_PANE_MIN_HEIGHT_PX) isAtMinimum[position] = true;
    });
  }
};

/**
 * Heights of the panes in an area of `areaHeight`: an expanded pane always
 * keeps its height (up to the whole area), the others shrink into what is
 * left; what does not fit is scrolled to.
 */
const fitHeights = (wanted, isExpanded, areaHeight) => {
  const expandedHeight = Math.min(INDICATOR_PANE_EXPANDED_HEIGHT_PX, areaHeight);
  const expandedCount = isExpanded.filter(Boolean).length;
  const restHeights = shrinkHeights(
    wanted.filter((height, position) => !isExpanded[position]),
    areaHeight - expandedCount * expandedHeight,
  );

  return wanted.map((height, position) => (isExpanded[position] ? expandedHeight : restHeights.shift()));
};

/**
 * Oscillator panes under the price chart (D24): each is INDICATOR_PANE_HEIGHT_PX,
 * Expand makes it INDICATOR_PANE_EXPANDED_HEIGHT_PX. The price chart keeps at
 * least MAIN_PANE_MIN_SHARE of the height: when the panes need more, the
 * expanded ones keep their height and the others shrink in proportion down
 * to INDICATOR_PANE_MIN_HEIGHT_PX; the panes that still do not fit are
 * scrolled to inside the area of the panes.
 *
 * @param {() => void} invalidate - schedules a frame
 */
export const createPaneStack = (invalidate) => {
  const expandedIds = new Set();
  let scrollTop = 0;
  let maxScrollTop = 0;

  return {
    /** @param {string} paneId */
    isExpanded: (paneId) => expandedIds.has(paneId),

    /**
     * @param {string} paneId - the key of the oscillator
     * @param {boolean} isExpanded
     */
    setExpanded: (paneId, isExpanded) => {
      if (isExpanded === expandedIds.has(paneId)) return;

      if (isExpanded) expandedIds.add(paneId);
      else expandedIds.delete(paneId);

      invalidate();
    },

    /** True while some panes do not fit and the area scrolls. */
    get canScroll() {
      return maxScrollTop > 0;
    },

    /**
     * @param {number} pixels - down when positive
     * @returns {boolean} false when already at that end
     */
    scrollBy: (pixels) => {
      const next = clamp(scrollTop + pixels, 0, maxScrollTop);

      if (next === scrollTop) return false;

      scrollTop = next;
      invalidate();

      return true;
    },

    /**
     * Places the panes of a frame.
     *
     * @param {number} plotHeight - CSS px above the time axis
     * @param {object[]} oscillators - indicators.oscillators, top to bottom
     * @returns {{ mainHeight: number, areaHeight: number, contentHeight: number, scrollTop: number,
     *   slots: Array<{ indicator: object, top: number, height: number, isExpanded: boolean }> }}
     *   the area of the panes starts at mainHeight; top: of a pane in the chart, after the scroll
     */
    arrange: (plotHeight, oscillators) => {
      const paneIds = new Set(oscillators.map((indicator) => indicator.paneId));

      expandedIds.forEach((paneId) => {
        if (!paneIds.has(paneId)) expandedIds.delete(paneId);
      });

      const isExpanded = oscillators.map((indicator) => expandedIds.has(indicator.paneId));
      const wanted = isExpanded.map((expanded) =>
        expanded ? INDICATOR_PANE_EXPANDED_HEIGHT_PX : INDICATOR_PANE_HEIGHT_PX,
      );
      const maxAreaHeight = Math.max(0, Math.floor(plotHeight * (1 - MAIN_PANE_MIN_SHARE)));
      const heights = sum(wanted) > maxAreaHeight ? fitHeights(wanted, isExpanded, maxAreaHeight) : wanted;
      const contentHeight = sum(heights);
      const areaHeight = Math.min(maxAreaHeight, contentHeight);
      const mainHeight = plotHeight - areaHeight;

      maxScrollTop = Math.max(0, contentHeight - areaHeight);
      scrollTop = clamp(scrollTop, 0, maxScrollTop);

      let top = mainHeight - scrollTop;
      const slots = oscillators.map((indicator, position) => {
        const slot = { indicator, top, height: heights[position], isExpanded: expandedIds.has(indicator.paneId) };

        top += heights[position];

        return slot;
      });

      return { mainHeight, areaHeight, contentHeight, scrollTop, slots };
    },
  };
};
